import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Col, Container, Dropdown, Form, Pagination, Row, Table } from "react-bootstrap";
import {
  fetchCompanyListing,
  fetchCompanyListingRelationships,
  fetchCompanyListings,
} from "../../utils/http/companies";
import { HamburgerToggle, JsonModal, normaliseResponse, unwrapId } from "./helpers";

// `source_entities` is an array of { source_system, source_system_id, ... };
// join the ids for display.
function sourceSystemIds(listing: any): string {
  const entities = listing?.source_entities;
  if (Array.isArray(entities)) {
    const ids = entities.map((e) => e?.source_system_id).filter(Boolean);
    return ids.length > 0 ? ids.join(", ") : "-";
  }
  return "-";
}

function formatAddress(listing: any): string {
  const address = listing?.property?.address;
  if (!address) return "-";
  const streetLine = [address.street, address.number].filter(Boolean).join(" ");
  const cityLine = [address.zip_code, address.city].filter(Boolean).join(" ");
  const parts = [streetLine, cityLine].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
}

function Listings({
  companyNameOrId,
  onViewAudit,
}: {
  companyNameOrId: string;
  onViewAudit: (entityType: string, entityId: string) => void;
}) {
  const [sourceSystemId, setSourceSystemId] = useState<string>("");
  const [street, setStreet] = useState<string>("");
  const [number, setNumber] = useState<string>("");
  const [debounced, setDebounced] = useState({ sourceSystemId: "", street: "", number: "" });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [relationshipsListingId, setRelationshipsListingId] = useState<string | null>(null);

  // Debounce the search inputs so we don't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced({ sourceSystemId, street, number });
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [sourceSystemId, street, number]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "company-listings",
      companyNameOrId,
      debounced.sourceSystemId,
      debounced.street,
      debounced.number,
      currentPage,
      pageSize,
    ],
    queryFn: ({ signal }) =>
      fetchCompanyListings({
        companyNameOrId,
        sourceSystemId: debounced.sourceSystemId || undefined,
        street: debounced.street || undefined,
        number: debounced.number || undefined,
        page: currentPage,
        size: pageSize,
        signal,
      }),
    staleTime: 2000,
  });

  const { items, total } = normaliseResponse(data);
  const totalPages = Math.ceil(total / pageSize);

  const {
    data: selectedListing,
    isLoading: isLoadingListing,
    error: listingError,
  } = useQuery({
    queryKey: ["company-listing", companyNameOrId, selectedListingId],
    queryFn: ({ signal }) =>
      fetchCompanyListing({
        companyNameOrId,
        listingId: selectedListingId!,
        signal,
      }),
    enabled: !!selectedListingId,
  });

  const {
    data: relationships,
    isLoading: isLoadingRelationships,
    error: relationshipsError,
  } = useQuery({
    queryKey: ["company-listing-relationships", companyNameOrId, relationshipsListingId],
    queryFn: ({ signal }) =>
      fetchCompanyListingRelationships({
        companyNameOrId,
        listingId: relationshipsListingId!,
        signal,
      }),
    enabled: !!relationshipsListingId,
  });

  return (
    <div>
      <Container className="pb-3 px-0">
        <Row className="mb-2">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Source System ID</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Exact match..."
                value={sourceSystemId}
                onChange={(e) => setSourceSystemId(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Street</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search by street..."
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Number</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Exact match..."
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <span className="text-muted">
              Showing {items.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
              {Math.min((currentPage - 1) * pageSize + items.length, total)} of {total} listings
            </span>
          </Col>
        </Row>
      </Container>
      {error ? (
        <div className="text-danger mb-3">
          {(error as any)?.message || "An error occurred while fetching the listings."}
        </div>
      ) : null}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}></th>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}>Source System ID</th>
            <th>Address</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={3} className="text-center text-muted">
                Loading...
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-center text-muted">
                No listings found.
              </td>
            </tr>
          ) : (
            items.map((listing: any, index: number) => {
              const listingId = unwrapId(listing?._id);
              return (
                <tr key={listingId !== "-" ? listingId : `${sourceSystemIds(listing)}-${index}`}>
                  <td style={{ width: "1%", whiteSpace: "nowrap", textAlign: "center" }}>
                    {listingId !== "-" && (
                      <Dropdown>
                        <Dropdown.Toggle as={HamburgerToggle} id={`listing-actions-${listingId}`} />
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setSelectedListingId(listingId)}>
                            <i className="bi bi-braces me-2"></i>
                            View JSON
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setRelationshipsListingId(listingId)}>
                            <i className="bi bi-diagram-3 me-2"></i>
                            View relationships
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => onViewAudit("listing", listingId)}>
                            <i className="bi bi-clock-history me-2"></i>
                            View audit logs
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </td>
                  <td style={{ width: "1%", whiteSpace: "nowrap" }}>{sourceSystemIds(listing)}</td>
                  <td>{formatAddress(listing)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
      <Container className="pb-3">
        <Row>
          <Col className="d-flex justify-content-center">
            {totalPages > 1 && (
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                    return (
                      <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <Pagination.Ellipsis key={`ellipsis-${page}`} disabled />;
                  }
                  return null;
                }).filter(Boolean)}

                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </Col>
        </Row>
      </Container>

      <JsonModal
        show={!!selectedListingId}
        onHide={() => setSelectedListingId(null)}
        title="Listing"
        isLoading={isLoadingListing}
        error={listingError}
        value={selectedListing}
      />

      <JsonModal
        show={!!relationshipsListingId}
        onHide={() => setRelationshipsListingId(null)}
        title="Listing Relationships"
        isLoading={isLoadingRelationships}
        error={relationshipsError}
        value={relationships}
      />
    </div>
  );
}

export default Listings;
